/**
 * PersonalQrCard — iter120.
 *
 * Profile-page card showing the current user's "personal" SplitEasy QR. Unlike
 * the per-debt QR (QrSettleSheet), this one has no group_id and no amount baked
 * in. When scanned, the payer's app asks them to pick which group + how much
 * to settle, then routes through the existing /coin-transfers/qr-settle endpoint.
 *
 * QR payload shape:
 *   {
 *     "v": 1,
 *     "kind": "spliteasy_user",
 *     "user_id": "<uuid>",
 *     "name":    "Hitesh",
 *     "ts":      1740000000     // issued-at, for stale-QR diagnostics only
 *   }
 *
 * Stays valid as long as the user_id does — no expiry, no rotation needed.
 * Safe to screenshot / share — exposes only public-ish identity (user_id +
 * display name) that any group co-member already sees in the app.
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Share2, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { buildQrUrl } from '../lib/qrPayload';

export default function PersonalQrCard({ user, defaultRevealed = false }) {
  const [revealed, setRevealed] = useState(defaultRevealed);
  if (!user?.id) return null;

  const payload = buildQrUrl({
    v: 1,
    kind: 'spliteasy_user',
    user_id: user.id,
    name: user.name || 'Member',
    ts: Math.floor(Date.now() / 1000),
  });

  const handleShare = async () => {
    const url = `${window.location.origin}/scan?u=${encodeURIComponent(user.id)}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Settle with ${user.name} on SplitEasy`,
          text: `Tap to settle with ${user.name} inside SplitEasy.`,
          url,
        });
        return;
      } catch (e) {
        if (e?.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied — share it on WhatsApp');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleDownload = () => {
    // Find the rendered <svg> and convert to PNG via a canvas.
    const svgEl = document.querySelector('[data-testid="personal-qr-svg"] svg');
    if (!svgEl) return;
    const xml = new XMLSerializer().serializeToString(svgEl);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const image64 = 'data:image/svg+xml;base64,' + svg64;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 720;
      canvas.height = 720;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 60, 60, 600, 600);
      const png = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = png;
      a.download = `spliteasy-qr-${(user.name || 'me').replace(/\s+/g, '-').toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('QR downloaded');
    };
    img.src = image64;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05 }}
    >
      <Card className="p-5 border-2 rounded-2xl mb-6" data-testid="personal-qr-card">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="font-display font-bold text-lg inline-flex items-center gap-1.5">
              <QrCode className="w-4 h-4" />
              Get paid — my QR
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Friends in your groups scan this to settle what they owe you. Only your group ledger updates — the group fund is never touched.
            </p>
          </div>
          {!revealed && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-full text-xs shrink-0"
              onClick={() => setRevealed(true)}
              data-testid="personal-qr-reveal"
            >
              Show QR
            </Button>
          )}
        </div>

        {revealed && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col items-center gap-3 pt-2"
          >
            <div
              className="p-4 bg-white border-2 border-border rounded-3xl"
              data-testid="personal-qr-svg"
            >
              <QRCodeSVG
                value={payload}
                size={220}
                level="M"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#0a0a0a"
              />
            </div>
            <div className="text-center">
              <p className="font-display text-lg font-bold">{user.name}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">
                Scan to settle · choose group + amount
              </p>
            </div>
            <div className="flex gap-2 w-full">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 rounded-full text-xs"
                onClick={handleDownload}
                data-testid="personal-qr-download"
              >
                <Download className="w-3.5 h-3.5 mr-1" /> Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 rounded-full text-xs"
                onClick={handleShare}
                data-testid="personal-qr-share"
              >
                <Share2 className="w-3.5 h-3.5 mr-1" /> Share
              </Button>
            </div>
          </motion.div>
        )}
      </Card>
    </motion.div>
  );
}
